import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { AccountBalanceWallet, HelpOutline, Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import DetailHeader from '../components/DetailHeader';
import { SUPPORTED_CURRENCY_CODES } from '../constants/currency';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import type { CurrencyCode } from '../types';
import {
  createPlanAdmin,
  fetchPlanAdmin,
  updatePlanAdmin,
} from '../services/subscriptions';

type BillingInterval = 'monthly' | 'quarterly' | 'semiannual' | 'annual';
type PlanType = 'ai' | 'reverse_ivr';

const defaultForm: {
  name: string;
  description: string;
  planType: PlanType;
  priceAmount: string;
  priceCurrency: CurrencyCode;
  setupFeeAmount: string;
  overageRateAmount: string;
  billingInterval: BillingInterval;
  maxMinutes: string;
  unlimitedMinutes: boolean;
  maxConcurrentCalls: string;
  unlimitedConcurrency: boolean;
  includedUsers: string;
  unlimitedUsers: boolean;
  includedAiAgents: string;
  unlimitedAiAgents: boolean;
  includedWhatsappAccounts: string;
  unlimitedWhatsappAccounts: boolean;
  enablePublicApi: boolean;
  isActive: boolean;
} = {
  name: '',
  description: '',
  planType: 'ai',
  priceAmount: '',
  priceCurrency: 'BRL',
  setupFeeAmount: '',
  overageRateAmount: '',
  billingInterval: 'monthly',
  maxMinutes: '',
  unlimitedMinutes: false,
  maxConcurrentCalls: '',
  unlimitedConcurrency: false,
  includedUsers: '',
  unlimitedUsers: false,
  includedAiAgents: '',
  unlimitedAiAgents: false,
  includedWhatsappAccounts: '',
  unlimitedWhatsappAccounts: false,
  enablePublicApi: false,
  isActive: true,
};

export default function PlanFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new' || id === 'undefined';
  const { formatCurrency } = useNumberFormat();
  usePageTitle(isNew ? t('pages:plans.new') : t('pages:plans.editTitle'));

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isReverseIvrPlan = form.planType === 'reverse_ivr';
  const resolvedPriceCurrency: CurrencyCode = SUPPORTED_CURRENCY_CODES.includes(form.priceCurrency)
    ? form.priceCurrency
    : 'BRL';

  useEffect(() => {
    if (isNew || !id) return;
    const loadPlan = async () => {
      try {
        setLoading(true);
        const plan = await fetchPlanAdmin(id as string);
        const priceCurrency = SUPPORTED_CURRENCY_CODES.includes(plan.priceCurrency as CurrencyCode)
          ? (plan.priceCurrency as CurrencyCode)
          : 'BRL';
        setForm({
          name: plan.name || '',
          description: plan.description || '',
          planType: plan.planType || 'ai',
          priceAmount: String(plan.priceAmount ?? ''),
          priceCurrency,
          setupFeeAmount: String(plan.setupFeeAmount ?? ''),
          overageRateAmount: String(plan.overageRateAmount ?? ''),
          billingInterval: plan.billingInterval || 'monthly',
          maxMinutes: plan.maxMinutesSeconds === -1 ? '' : String(plan.maxMinutesSeconds / 60),
          unlimitedMinutes: plan.maxMinutesSeconds === -1,
          maxConcurrentCalls: plan.maxConcurrentCalls === -1 ? '' : String(plan.maxConcurrentCalls),
          unlimitedConcurrency: plan.maxConcurrentCalls === -1,
          includedUsers: plan.includedUsers === -1 ? '' : String(plan.includedUsers ?? ''),
          unlimitedUsers: plan.includedUsers === -1,
          includedAiAgents: plan.includedAiAgents === -1 ? '' : String(plan.includedAiAgents ?? ''),
          unlimitedAiAgents: plan.includedAiAgents === -1,
          includedWhatsappAccounts: plan.includedWhatsappAccounts === -1
            ? ''
            : String(plan.includedWhatsappAccounts ?? ''),
          unlimitedWhatsappAccounts: plan.includedWhatsappAccounts === -1,
          enablePublicApi: Boolean(plan.enablePublicApi),
          isActive: plan.isActive,
        });
      } catch (err) {
        console.error('Error loading plan:', err);
        setError(t('pages:plans.errors.load'));
      } finally {
        setLoading(false);
      }
    };

    loadPlan();
  }, [id, isNew, t]);

  const breadcrumbs = useMemo(
    () => [
      { label: t('common:menu.dashboard'), to: '/' },
      { label: t('pages:plans.title'), to: '/subscriptions/plans' },
      { label: isNew ? t('pages:plans.new') : t('pages:plans.editTitle') },
    ],
    [isNew, t]
  );

  const handleChange = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        description: form.description || '',
        plan_type: form.planType,
        price_amount: Number(form.priceAmount || 0),
        price_currency: resolvedPriceCurrency,
        setup_fee_amount: Number(form.setupFeeAmount || 0),
        overage_rate_amount: isReverseIvrPlan ? 0 : Number(form.overageRateAmount || 0),
        billing_interval: form.billingInterval,
        max_minutes_seconds: isReverseIvrPlan
          ? 0
          : (form.unlimitedMinutes ? -1 : Number(form.maxMinutes || 0) * 60),
        max_concurrent_calls: form.unlimitedConcurrency ? -1 : Number(form.maxConcurrentCalls || 0),
        included_users: form.unlimitedUsers ? -1 : Number(form.includedUsers || 0),
        included_ai_agents: isReverseIvrPlan
          ? 0
          : (form.unlimitedAiAgents ? -1 : Number(form.includedAiAgents || 0)),
        included_whatsapp_accounts: form.unlimitedWhatsappAccounts
          ? -1
          : Number(form.includedWhatsappAccounts || 0),
        enable_public_api: form.enablePublicApi,
        is_active: form.isActive,
      };

      if (isNew) {
        await createPlanAdmin(payload);
      } else {
        await updatePlanAdmin(id as string, payload);
      }
      navigate('/subscriptions/plans');
    } catch (err) {
      console.error('Error saving plan:', err);
      setError(t('pages:plans.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <DetailHeader
          sectionIcon={<AccountBalanceWallet />}
          title={t('pages:plans.title')}
          breadcrumbs={breadcrumbs}
        />
        <Typography color="text.secondary">{t('common:common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        sectionIcon={<AccountBalanceWallet />}
        title={isNew ? t('pages:plans.new') : t('pages:plans.editTitle')}
        breadcrumbs={breadcrumbs}
        actions={(
          <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        )}
      />

      <Card>
        <CardContent>
          <Stack spacing={3}>
            {error && <Typography color="error">{error}</Typography>}
            <TextField
              label={t('common:common.name')}
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
            />
            <TextField
              label={t('common:common.description')}
              value={form.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              minRows={3}
            />
            <FormControl fullWidth>
              <InputLabel>{t('pages:plans.fields.planType')}</InputLabel>
              <Select
                value={form.planType}
                label={t('pages:plans.fields.planType')}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    planType: event.target.value as PlanType,
                  }))
                }
              >
                <MenuItem value="ai">{t('pages:plans.planTypes.ai')}</MenuItem>
                <MenuItem value="reverse_ivr">{t('pages:plans.planTypes.reverse_ivr')}</MenuItem>
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('pages:plans.fields.price')}
                value={form.priceAmount}
                onChange={handleChange('priceAmount')}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>{t('pages:plans.fields.currency')}</InputLabel>
                <Select
                  value={resolvedPriceCurrency}
                  label={t('pages:plans.fields.currency')}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      priceCurrency: event.target.value as CurrencyCode,
                    }))
                  }
                >
                  {SUPPORTED_CURRENCY_CODES.map((currencyCode) => (
                    <MenuItem key={currencyCode} value={currencyCode}>
                      {currencyCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('pages:plans.fields.setupFee')}
                value={form.setupFeeAmount}
                onChange={handleChange('setupFeeAmount')}
                fullWidth
              />
              {!isReverseIvrPlan && (
                <TextField
                  label={t('pages:plans.fields.overageRate')}
                  value={form.overageRateAmount}
                  onChange={handleChange('overageRateAmount')}
                  fullWidth
                  helperText={formatCurrency(Number(form.overageRateAmount || 0), resolvedPriceCurrency)}
                />
              )}
            </Stack>
            <FormControl fullWidth>
              <InputLabel>{t('pages:plans.fields.billingInterval')}</InputLabel>
              <Select
                value={form.billingInterval}
                label={t('pages:plans.fields.billingInterval')}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    billingInterval: event.target.value as BillingInterval,
                  }))
                }
              >
                <MenuItem value="monthly">{t('pages:plans.intervals.monthly')}</MenuItem>
                <MenuItem value="quarterly">{t('pages:plans.intervals.quarterly')}</MenuItem>
                <MenuItem value="semiannual">{t('pages:plans.intervals.semiannual')}</MenuItem>
                <MenuItem value="annual">{t('pages:plans.intervals.annual')}</MenuItem>
              </Select>
            </FormControl>
            <Divider />
            {!isReverseIvrPlan && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('pages:plans.fields.maxMinutes')}
                  value={form.maxMinutes}
                  onChange={handleChange('maxMinutes')}
                  fullWidth
                  disabled={form.unlimitedMinutes}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.unlimitedMinutes}
                      onChange={(event) => setForm((prev) => ({
                        ...prev,
                        unlimitedMinutes: event.target.checked,
                      }))}
                    />
                  )}
                  label={t('pages:plans.fields.unlimitedMinutes')}
                />
              </Stack>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('pages:plans.fields.maxConcurrentCalls')}
                value={form.maxConcurrentCalls}
                onChange={handleChange('maxConcurrentCalls')}
                fullWidth
                disabled={form.unlimitedConcurrency}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.unlimitedConcurrency}
                    onChange={(event) => setForm((prev) => ({
                      ...prev,
                      unlimitedConcurrency: event.target.checked,
                    }))}
                  />
                )}
                label={t('pages:plans.fields.unlimitedConcurrency')}
              />
            </Stack>
            <Divider />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('pages:plans.fields.includedUsers')}
                value={form.includedUsers}
                onChange={handleChange('includedUsers')}
                fullWidth
                disabled={form.unlimitedUsers}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.unlimitedUsers}
                    onChange={(event) => setForm((prev) => ({
                      ...prev,
                      unlimitedUsers: event.target.checked,
                    }))}
                  />
                )}
                label={t('pages:plans.fields.unlimitedUsers')}
              />
              {!isReverseIvrPlan && (
                <>
                  <TextField
                    label={t('pages:plans.fields.includedAiAgents')}
                    value={form.includedAiAgents}
                    onChange={handleChange('includedAiAgents')}
                    fullWidth
                    disabled={form.unlimitedAiAgents}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={form.unlimitedAiAgents}
                        onChange={(event) => setForm((prev) => ({
                          ...prev,
                          unlimitedAiAgents: event.target.checked,
                        }))}
                      />
                    )}
                    label={t('pages:plans.fields.unlimitedAiAgents')}
                  />
                </>
              )}
              <TextField
                label={t('pages:plans.fields.includedWhatsapp')}
                value={form.includedWhatsappAccounts}
                onChange={handleChange('includedWhatsappAccounts')}
                fullWidth
                disabled={form.unlimitedWhatsappAccounts}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.unlimitedWhatsappAccounts}
                    onChange={(event) => setForm((prev) => ({
                      ...prev,
                      unlimitedWhatsappAccounts: event.target.checked,
                    }))}
                  />
                )}
                label={t('pages:plans.fields.unlimitedWhatsapp')}
              />
            </Stack>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label={t('pages:plans.fields.isActive')}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.enablePublicApi}
                    onChange={(event) => setForm((prev) => ({
                      ...prev,
                      enablePublicApi: event.target.checked,
                    }))}
                  />
                )}
                label={t('pages:plans.fields.enablePublicApi')}
              />
              <Tooltip title={t('pages:plans.fields.enablePublicApiHelp')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
